import { useState } from "react";
import { PasswordInput } from "@/components/shop/PasswordInput";
import { Link } from "react-router-dom";
import { Building2, User } from "lucide-react";
import { EU_COUNTRIES } from "@/lib/constants";
import { fieldRules } from "@/lib/validation";

const inp = "h-11 w-full rounded-xl border border-black/10 bg-[#FFFBF3] px-3 text-sm outline-none transition-colors focus:border-amber-500/60";
const F = ({ label, className = "", ...props }) => (
  <label className={`block space-y-1 ${className}`}>
    <span className="text-xs text-stone-600">{label}</span>
    {props.type === "password" ? <PasswordInput {...props} className={inp} /> : <input {...props} className={inp} />}
  </label>
);

const ODD = "1 0 5 7 9 13 15 17 19 21 1 0 5 7 9 13 15 17 19 21 2 4 18 20 11 3 6 8 12 14 16 10 22 25 24 23".split(" ").map(Number);
const CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const validCF = (cf) => {
  if (!/^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-EHLMPR-T][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/.test(cf)) return false;
  const s = [...cf.slice(0, 15)].reduce((a, c, i) => a + (i % 2 === 0 ? ODD[CHARS.indexOf(c)] : /\d/.test(c) ? +c : c.charCodeAt(0) - 65), 0);
  return String.fromCharCode(65 + (s % 26)) === cf[15];
};
export const validItVat = (v) => {
  if (!/^\d{11}$/.test(v) || /^0+$/.test(v)) return false;
  const sum = [...v.slice(0, 10)].reduce((a, ch, i) => { let n = +ch; if (i % 2) { n *= 2; if (n > 9) n -= 9; } return a + n; }, 0);
  return (10 - (sum % 10)) % 10 === +v[10];
};

const EMPTY = { first_name: "", last_name: "", email: "", password: "", tax_code: "", accept_terms: false, accept_privacy: false,
  company: "", country: "Italia", vat: "", address: "", zip: "", city: "", province: "", phone: "", sdi: "", pec: "" };

const clientError = (t, f) => {
  if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(f.password)) return "La password deve avere almeno 8 caratteri, con almeno una lettera e un numero";
  const cf = f.tax_code.trim().toUpperCase();
  if (cf && !validCF(cf)) return "Codice fiscale non valido: controlla i 16 caratteri";
  if (t !== "azienda") return null;
  const it = f.country === "Italia";
  if (it && !validItVat(f.vat.replace(/\s/g, "").replace(/^IT/i, ""))) return "Partita IVA non valida: 11 cifre, controlla la cifra finale";
  if (it && !cf) return "Inserisci il codice fiscale dell'amministratore o referente";
  if (it && !f.sdi.trim() && !f.pec.trim()) return "Inserisci il Codice Destinatario SDI oppure la PEC";
  return null;
};

export const RegisterForm = ({ initialType = "privato", onSubmit, loading, setError }) => {
  const [t, setT] = useState(initialType);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const biz = t === "azienda";
  const it = f.country === "Italia";
  const submit = (e) => {
    e.preventDefault();
    const err = clientError(t, f);
    if (err) return setError(err);
    const body = { ...f, account_type: t, tax_code: f.tax_code.trim().toUpperCase() || null, lang: localStorage.getItem("cs_lang") || "it" };
    if (!biz) ["company", "vat", "address", "zip", "city", "province", "phone", "sdi", "pec"].forEach((k) => delete body[k]);
    else ["sdi", "pec", "province"].forEach((k) => { if (!body[k].trim()) body[k] = null; });
    onSubmit(body);
  };
  const tab = (v, Icon, label) => (
    <button type="button" onClick={() => { setT(v); setError(""); }} data-testid={`register-type-${v}`} aria-pressed={t === v}
      className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${t === v ? "border-black bg-amber-500 text-black" : "border-black/10 bg-white text-stone-600 hover:border-black/30"}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );
  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid={`register-form-${t}`}>
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Tipo di account">{tab("privato", User, "Privato")}{tab("azienda", Building2, "Azienda / P.IVA")}</div>
      {biz && <p className="rounded-lg bg-cyan-700/10 px-3 py-2 text-xs text-cyan-900" data-testid="register-b2b-note">Dopo la registrazione verifichiamo i dati: finché non approviamo l'accesso B2B vedi il negozio come privato. Ti avvisiamo via email.</p>}
      {biz && <>
        <F label="Ragione sociale *" required value={f.company} onChange={set("company")} {...fieldRules("company", f.country)} data-testid="register-company" />
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1"><span className="text-xs text-stone-600">Paese della sede *</span>
            <select value={f.country} onChange={set("country")} className={inp} data-testid="register-country">{EU_COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <F label={it ? "Partita IVA * (11 cifre)" : "Partita IVA / VAT *"} required value={f.vat} onChange={set("vat")} inputMode={it ? "numeric" : "text"} data-testid="register-vat" />
        </div>
      </>}
      <div className="grid grid-cols-2 gap-3">
        <F label={biz ? "Nome referente *" : "Nome *"} required autoComplete="given-name" value={f.first_name} onChange={set("first_name")} {...fieldRules("first_name")} data-testid="register-first-name" />
        <F label={biz ? "Cognome referente *" : "Cognome *"} required autoComplete="family-name" value={f.last_name} onChange={set("last_name")} {...fieldRules("last_name")} data-testid="register-last-name" />
      </div>
      <F label={biz ? `Codice fiscale amministratore/referente${it ? " *" : ""}` : "Codice fiscale (facoltativo, serve per la fattura)"} required={biz && it} maxLength={16}
        value={f.tax_code} onChange={(e) => setF({ ...f, tax_code: e.target.value.toUpperCase() })} data-testid="register-tax-code" />
      {biz && <>
        <F label="Indirizzo di fatturazione *" required value={f.address} onChange={set("address")} {...fieldRules("address", f.country)} data-testid="register-address" />
        <div className="grid grid-cols-3 gap-3">
          <F label="CAP *" required value={f.zip} onChange={set("zip")} {...fieldRules("zip", f.country)} data-testid="register-zip" />
          <F label="Città *" required value={f.city} onChange={set("city")} className="col-span-2" {...fieldRules("city", f.country)} data-testid="register-city" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label={`Provincia${it ? " * (sigla)" : ""}`} required={it} maxLength={2} value={f.province} onChange={(e) => setF({ ...f, province: e.target.value.toUpperCase() })} {...fieldRules("province", f.country)} data-testid="register-province" />
          <F label="Telefono *" required type="tel" value={f.phone} onChange={set("phone")} {...fieldRules("phone", f.country)} data-testid="register-phone" />
        </div>
        {it && <div className="grid grid-cols-2 gap-3">
          <F label="Codice Destinatario SDI" maxLength={7} value={f.sdi} onChange={(e) => setF({ ...f, sdi: e.target.value.toUpperCase() })} data-testid="register-sdi" />
          <F label="oppure PEC" type="email" value={f.pec} onChange={set("pec")} data-testid="register-pec" />
        </div>}
      </>}
      <F label={biz ? "Email aziendale *" : "Email *"} type="email" required autoComplete="email" value={f.email} onChange={set("email")} data-testid="register-email-input" />
      <F label="Password * (min. 8 caratteri, lettere e numeri)" type="password" required minLength={8} autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="register-password-input" />
      <label className="flex items-start gap-2 text-sm text-stone-700">
        <input type="checkbox" required checked={f.accept_terms} onChange={set("accept_terms")} className="mt-1" data-testid="register-accept-terms" />
        <span>Ho letto e accetto i <Link to="/condizioni-di-vendita" className="text-cyan-700 underline">Termini e condizioni di vendita</Link> *</span>
      </label>
      <label className="flex items-start gap-2 text-sm text-stone-700">
        <input type="checkbox" required checked={f.accept_privacy} onChange={set("accept_privacy")} className="mt-1" data-testid="register-accept-privacy" />
        <span>Ho letto l'<Link to="/privacy" className="text-cyan-700 underline">Informativa privacy</Link> e acconsento al trattamento dei miei dati per gestire l'account e gli ordini *</span>
      </label>
      <button disabled={loading} className="h-12 w-full rounded-full bg-amber-500 font-display font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60" data-testid="register-submit-button">
        {loading ? "Creazione..." : biz ? "Registra l'azienda" : "Crea account"}
      </button>
    </form>
  );
};
